import React from 'react';
import { useMatch } from 'react-router-dom';
import * as AppRoutes from 'src/routing/constants/routes';
import { UnloadPages, UnloadRoute } from 'src/store/common-slices/unloadSlice';
import UnloadDoorScanPage from 'src/components/pages/unload-page/scan-door-page/UnloadDoorScanPageContainer';
import { UnloadCallPending } from 'src/components/pages/unload-page/UnloadCallPending';
import UnloadContainerScanPage
    from 'src/components/pages/unload-page/scan-container-page/UnloadContainerScanPageContainer';
import UnloadResolveSidelinePage
    from 'src/components/pages/unload-page/resolve-sideline-page/UnloadResolveSidelinePageContainer';
import { lockZoomOnSmallScreens } from 'src/utils/viewport-scaling';
import { PageChrome, useSetPageChrome } from 'src/components/app/page-chrome/PageChrome';
import UnloadFooter from 'src/components/pages/unload-page/unload-footer/UnloadFooterContainer';
import './Unload.scss';

// Steps switch here rather than by route, so the trailer context survives between them
const STEP_COMPONENTS: Record<UnloadPages, React.ComponentType> = {
    [UnloadPages.SCAN_DOOR_PAGE]: UnloadDoorScanPage,
    [UnloadPages.SCAN_CONTAINER_PAGE]: UnloadContainerScanPage,
    // The orphan loop is the container loop with no trailer
    [UnloadPages.SCAN_ORPHAN_PAGE]: UnloadContainerScanPage,
    // Sideline's step is drawn by the loop's container, so over the loop it stays mounted and keeps its notice timer
    [UnloadPages.SCAN_SIDELINE_PAGE]: UnloadContainerScanPage,
    // Resolve Sideline's two steps share their container, which picks the step
    [UnloadPages.SCAN_SIDELINE_TO_RESOLVE_PAGE]: UnloadResolveSidelinePage,
    [UnloadPages.SCAN_OWN_LABEL_PAGE]: UnloadResolveSidelinePage
};

// The steps fit the smallest screens, and their footer has each step's own items
const CHROME: PageChrome = { compact: true, footer: <UnloadFooter/> };

interface UnloadProps {
    // The step on screen
    pageToRender: UnloadPages;
    // A call is in flight
    callPending: boolean;
    // Called on each visit with its route: it clears the last results, and starts afresh when the last visit was on
    // another unload route
    enterUnloadRoute: (route: UnloadRoute) => void;
    // Called when the page unmounts, so an answer that lands then shows nothing
    leaveUnloadRoute: () => void;
}

// In staging the door step scans a pallet, and the loop scans the cartons inside it
const Unload = ({ pageToRender, enterUnloadRoute, leaveUnloadRoute, callPending }: UnloadProps) => {
    const Step = STEP_COMPONENTS[pageToRender];
    const resolving = useMatch(AppRoutes.UNLOAD_RESOLVE_PAGE_ROUTE) !== null;
    const route = useMatch(AppRoutes.UNLOAD_PALLET_PAGE_ROUTE) ? UnloadRoute.PALLET
        : resolving ? UnloadRoute.RESOLVE_SIDELINE : UnloadRoute.TRAILER;
    useSetPageChrome(CHROME);
    React.useEffect(lockZoomOnSmallScreens, []);
    React.useEffect(() => {
        enterUnloadRoute(route);
        return leaveUnloadRoute;
    }, [enterUnloadRoute, leaveUnloadRoute, route]);
    return (
        <div className="unload">
            <UnloadCallPending.Provider value={callPending}>
                <Step/>
            </UnloadCallPending.Provider>
        </div>
    );
};

export default Unload;
